import { useState } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import {
    generateLMPData,
    generateLMPData1,
    generateLMPData2,
    generateLMPData3,
} from "./MarketInsightsData";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../ui/DropdownMenu";
import { MarketInsightsChartTooltip } from "./MarketInsightsChartTooltip";

type DatasetType = "default" | "dataset1" | "dataset2" | "dataset3";

export function MarketInsightsLMPChart() {
    const [selectedDataset, setSelectedDataset] =
        useState<DatasetType>("default");

    const getData = () => {
        switch (selectedDataset) {
            case "dataset1":
                return generateLMPData1();
            case "dataset2":
                return generateLMPData2();
            case "dataset3":
                return generateLMPData3();
            default:
                return generateLMPData();
        }
    };

    const data = getData();

    const totalPoints = 288; // Assuming fixed total points for 24 hours
    const tickInterval = Math.floor(totalPoints / 4);

    const formatXAxisTick = (value: string) => {
        if (value === `Point 1`) {
            return "Jun 16";
        } else if (value === `Point ${tickInterval + 1}`) {
            return "6a";
        } else if (value === `Point ${tickInterval * 2 + 1}`) {
            return "12p";
        } else if (value === `Point ${tickInterval * 3 + 1}`) {
            return "6p";
        } else if (value === `Point ${totalPoints}`) {
            return "Jun 17";
        }
        return "";
    };

    const ticks = [
        `Point 1`,
        `Point ${tickInterval + 1}`,
        `Point ${tickInterval * 2 + 1}`,
        `Point ${tickInterval * 3 + 1}`,
        `Point ${totalPoints}`,
    ];

    return (
        <div className="rounded-[4px] dark:bg-[#2D3748] bg-white w-full h-full flex flex-auto flex-col">
            <div className="flex-1">
                <ResponsiveContainer width="100%" height={320}>
                    <LineChart
                        width={500}
                        height={300}
                        data={data}
                        margin={{
                            top: 5,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid 
                            strokeDasharray="3 3" 
                            stroke="currentColor"
                            className="text-gray-400 dark:text-gray-700"
                        />
                        <XAxis
                            dataKey="name"
                            tickFormatter={formatXAxisTick}
                            ticks={ticks}
                            type="category"
                            tickLine={false}
                            axisLine={true}
                            stroke="currentColor"
                            className="text-gray-600 dark:text-gray-400"
                        />
                        <YAxis
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(value) => `$${value}`}
                            stroke="currentColor"
                            className="text-gray-600 dark:text-gray-400"
                        />
                        <Tooltip content={<MarketInsightsChartTooltip unit="$" />} />
                        <Legend 
                            verticalAlign="bottom"
                            height={36}
                            iconType="line"
                            wrapperStyle={{
                            paddingTop: '20px',
                            fontSize: '14px'
                            }}
                            formatter={(value) => (
                            <span className="text-neutral-600 dark:text-[#CEE0EF]">{value}</span>
                            )}
                        />
                        <Line
                            type="step"
                            dataKey="pv"
                            name="Real Time"
                            stroke="#82ca9d"
                            dot={false}
                        />
                        <Line
                            type="step"
                            dataKey="uv"
                            name="Day Ahead"
                            stroke="#8884d8"
                            dot={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="flex justify-end mb-2">
                <DropdownMenu>
                    <DropdownMenuTrigger className="flex flex-auto justify-center items-center px-3 py-1.5 mt-4 text-sm font-semibold rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600">
                        {selectedDataset === "default"
                            ? "Default Dataset"
                            : selectedDataset === "dataset1"
                              ? "Dataset 1"
                              : selectedDataset === "dataset2"
                                ? "Dataset 2"
                                : "Dataset 3"}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        <DropdownMenuItem
                            onClick={() => setSelectedDataset("default")}
                        >
                            Default Dataset
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => setSelectedDataset("dataset1")}
                        >
                            Dataset 1 (Higher Volatility)
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => setSelectedDataset("dataset2")}
                        >
                            Dataset 2 (Longer Steps)
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => setSelectedDataset("dataset3")}
                        >
                            Dataset 3 (More Spikes)
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}

export default MarketInsightsLMPChart;
